import './assets/main.scss';

import { createApp, type Plugin } from 'vue';
import { createI18n } from 'vue-i18n';
import { FontAwesomeIcon } from './libs/FontAwesomeIcons';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import ToastService from 'primevue/toastservice';
import App from './App.vue';
import router from './router';
import vRipple from './directives/vRipple';
import { createPinia } from 'pinia';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import { AppPreset } from './assets/AppPreset';
import StudySettingsService from './services/studySettingsService';
import StudyUsersService from './services/studyUsersService';
import StudyGroupService from './services/studyGroupService';
import InviteService from './services/inviteService';
import UserService from './services/userService';
import StudyService from './services/studyService';
import StudyLinkService from './services/studyLinkService';
import FormService from './services/formService';
import ExportService from './services/exportService';
import ActivityService from './services/activityService';
import BlobService from './services/blobService';
import CaseService from './services/caseService';
import EntryService from './services/entryService';
import en from '../locales/en.json';
import es from '../locales/es.json';
import * as Sentry from '@sentry/vue';
import { LoginService, DeepLinkService } from './services';
import AnalyticsService from './services/AnalyticsService';
import SurgeonDashboardService from './services/SurgeonDashboardService';
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import { VueTelInputDefaultConfig } from '@/utils';

try {
  LoginService.initService();
  DeepLinkService.initService();
  StudySettingsService.initService();
  StudyUsersService.initService('investigation', 'study');
  StudyGroupService.initService('investigation', 'group');
  StudyService.initService('investigation');
  StudyLinkService.initService('investigation');
  InviteService.initService('invite');
  UserService.initService('user');
  FormService.initService('investigation', 'survey');
  ExportService.inviteService('investigation', 'export');
  ActivityService.initService('investigation');
  BlobService.initService('blob');
  CaseService.initService('study', 'investigation', 'case');
  EntryService.initService('study');
  AnalyticsService.initService();
  SurgeonDashboardService.initService();

  const messages = { en, es };

  const i18n = createI18n({
    legacy: false,
    locale: 'en', // Default language
    fallbackLocale: 'en', // Fallback language
    messages
  });

  const app = createApp(App);

  //Adding sentry support
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/api-next(-staging)?\.teamscope\.co/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.VITE_APP_ENVIRONMENT || 'staging'
  });

  app.component('FontAwesomeIcon', FontAwesomeIcon);

  app.use(router);
  app.use(PrimeVue, {
    theme: {
      preset: AppPreset,
      options: {
        darkModeSelector: 'none'
      }
    }
  });
  app.use(ToastService);
  app.use(VueReCaptcha, { siteKey: import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY, loaderOptions: {} });

  app.directive('ripple', vRipple);
  app.directive('tooltip', Tooltip);

  app.use(createPinia());
  app.use(i18n);
  app.use(VueTelInput as unknown as Plugin, VueTelInputDefaultConfig);
  app.mount('#app');
} catch (error) {
  Sentry.captureException(error);
}
